import { useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactoPage() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    porque_agora: '',
    qual_ruido: '',
    o_que_procura: '',
    como_encontrou: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
    } catch (err) {
      setError('Ocorreu um erro ao enviar. Por favor, tente novamente.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <main className="pt-24 min-h-screen flex items-center" data-testid="contacto-success">
        <div className="page-container">
          <div className="max-w-2xl mx-auto text-center">
            <CheckCircle size={48} className="text-[#1A1A1A] mx-auto mb-6" />
            <h1 className="heading-section mb-6">
              Mensagem recebida.
            </h1>
            <p className="body-text mb-4">
              Obrigado por dedicar tempo a responder a estas perguntas.
            </p>
            <p className="body-text">
              Entrarei em contacto nos próximos dias para agendar 
              uma conversa inicial, se fizer sentido para ambos.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24" data-testid="contacto-page">
      {/* Hero */}
      <section className="section">
        <div className="page-container">
          <div className="max-w-3xl">
            <span className="label-mono mb-4 block">Contacto</span>
            <h1 className="heading-display mb-8">
              Conversa Inicial
            </h1>
            <p className="body-text text-lg">
              O primeiro passo é sempre uma conversa. 
              Não existe compra direta — existe reflexão mútua 
              sobre se faz sentido trabalharmos juntos.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section section-muted" data-testid="form-section">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Name */}
                <div>
                  <label htmlFor="nome" className="label-mono mb-2 block">
                    Nome
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Como prefere ser tratado"
                    data-testid="input-nome"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="label-mono mb-2 block">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Para resposta"
                    data-testid="input-email"
                  />
                </div>

                {/* Reflective Questions */}
                <div className="pt-6 border-t border-[#E6E4DC]">
                  <p className="body-text mb-6">
                    As perguntas seguintes ajudam-me a perceber o contexto 
                    antes da nossa conversa. Responda com a profundidade 
                    que lhe parecer adequada.
                  </p>
                </div>

                {/* Why Now */}
                <div>
                  <label htmlFor="porque_agora" className="label-mono mb-2 block">
                    Porquê agora?
                  </label>
                  <p className="text-sm text-[#5A5A5A] mb-3">
                    O que o traz a procurar este tipo de acompanhamento neste momento?
                  </p>
                  <textarea
                    id="porque_agora"
                    name="porque_agora"
                    value={formData.porque_agora}
                    onChange={handleChange}
                    required
                    className="form-textarea"
                    placeholder="Descreva o momento atual..."
                    data-testid="input-porque-agora"
                  />
                </div>

                {/* The Noise */}
                <div>
                  <label htmlFor="qual_ruido" className="label-mono mb-2 block">
                    Qual é o ruído?
                  </label>
                  <p className="text-sm text-[#5A5A5A] mb-3">
                    O que está a dificultar a clareza ou a decisão?
                  </p>
                  <textarea
                    id="qual_ruido"
                    name="qual_ruido"
                    value={formData.qual_ruido}
                    onChange={handleChange}
                    required
                    className="form-textarea"
                    placeholder="Identifique o que está a criar confusão..."
                    data-testid="input-qual-ruido"
                  />
                </div>

                {/* What You Seek */}
                <div>
                  <label htmlFor="o_que_procura" className="label-mono mb-2 block">
                    O que procura?
                  </label>
                  <p className="text-sm text-[#5A5A5A] mb-3">
                    O que seria diferente se este acompanhamento funcionasse?
                  </p>
                  <textarea
                    id="o_que_procura"
                    name="o_que_procura"
                    value={formData.o_que_procura}
                    onChange={handleChange}
                    required
                    className="form-textarea"
                    placeholder="Descreva o que procura alcançar..."
                    data-testid="input-o-que-procura"
                  />
                </div>

                {/* How Found (Optional) */}
                <div>
                  <label htmlFor="como_encontrou" className="label-mono mb-2 block">
                    Como me encontrou? <span className="text-[#5A5A5A]">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    id="como_encontrou"
                    name="como_encontrou"
                    value={formData.como_encontrou}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Referência, pesquisa, etc."
                    data-testid="input-como-encontrou"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="text-red-600 text-sm" data-testid="form-error">
                    {error}
                  </div>
                )}

                {/* Submit */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full sm:w-auto"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        A enviar...
                      </>
                    ) : (
                      'Enviar Mensagem'
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Side Info */}
            <div className="lg:pt-12">
              <div className="sticky top-32">
                <span className="label-mono mb-4 block">O que esperar</span>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="font-serif text-xl text-[#1A1A1A] mb-2">
                      Resposta em 48 horas
                    </h3>
                    <p className="body-text">
                      Responderei ao seu contacto em 48 horas úteis, 
                      tipicamente para agendar uma conversa inicial.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-serif text-xl text-[#1A1A1A] mb-2">
                      Conversa sem compromisso
                    </h3>
                    <p className="body-text">
                      A primeira conversa serve para perceber se há fit. 
                      Não há pressão para avançar.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-serif text-xl text-[#1A1A1A] mb-2">
                      Confidencialidade
                    </h3>
                    <p className="body-text">
                      As suas respostas são confidenciais e servem apenas 
                      para preparar a nossa conversa.
                    </p>
                  </div>
                </div>

                <div className="mt-12 pt-6 border-t border-[#E6E4DC]">
                  <span className="label-mono mb-2 block">Contacto direto</span>
                  <a 
                    href="mailto:contacto@orafaelpinto.com" 
                    className="body-text hover:text-[#1A1A1A] transition-colors"
                    data-testid="email-link"
                  >
                    contacto@orafaelpinto.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
